import { logger } from '../utils/logger';
import React, { useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Search, 
  ExternalLink,
  BookOpen,
  Loader2,
  GraduationCap,
  Sparkles,
  Database,
  Globe
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../config/api';

const EduSearch = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchMode, setSearchMode] = useState('curated'); // curated or live
  const [searchSource, setSearchSource] = useState(null);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;

    setLoading(true);
    setSearched(true);
    
    try {
      const endpoint = searchMode === 'live' 
        ? `${BACKEND_URL}/api/neuro/edu-search-live`
        : `${BACKEND_URL}/api/neuro/edu-search`;
      
      const { data } = await axios.post(
        endpoint,
        { query },
        { withCredentials: true }
      );
      setResults(data.results || []);
      setSearchSource(data.source);
      
      if (searchMode === 'live' && data.source === 'live') {
        toast.success('Live .edu search complete');
      }
    } catch (error) {
      logger.error('Search error:', error);
      toast.error('Search failed. Try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const suggestedSearches = [
    'brain tumor',
    'vestibular disease',
    'spinal cord',
    'meningoencephalitis',
    'IVDD',
    'seizures',
    'degenerative myelopathy',
    'hydrocephalus'
  ];

  return (
    <div className="space-y-6 animate-fade-in" data-testid="edu-search-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-semibold text-[#2A2C2A] font-['Work_Sans']">
          .edu Resource Search
        </h1>
        <p className="text-[#5C5F5C] mt-1">
          Search veterinary neurology knowledge from accredited institutions
        </p>
      </div>

      {/* Search Mode Tabs */}
      <Tabs value={searchMode} onValueChange={setSearchMode}>
        <TabsList className="bg-white border border-[#E5E4E0] p-1">
          <TabsTrigger 
            value="curated" 
            className="data-[state=active]:bg-[#4B6651] data-[state=active]:text-white"
            data-testid="tab-curated"
          >
            <Database className="w-4 h-4 mr-2" />
            Curated Database
          </TabsTrigger>
          <TabsTrigger 
            value="live" 
            className="data-[state=active]:bg-[#C06144] data-[state=active]:text-white"
            data-testid="tab-live"
          >
            <Globe className="w-4 h-4 mr-2" />
            Live AI Search
          </TabsTrigger>
        </TabsList>

        <TabsContent value="curated" className="mt-4">
          <p className="text-sm text-[#5C5F5C] mb-4">
            Search our curated database of verified veterinary neurology information from .edu sources.
          </p>
        </TabsContent>
        <TabsContent value="live" className="mt-4">
          <div className="flex items-center gap-2 p-3 bg-[#C06144]/10 rounded-lg mb-4">
            <Sparkles className="w-5 h-5 text-[#C06144]" />
            <p className="text-sm text-[#5C5F5C]">
              <strong className="text-[#C06144]">AI-Powered:</strong> Uses GPT-5.2 to search and synthesize information from veterinary .edu sources in real-time.
            </p>
          </div>
        </TabsContent>
      </Tabs>

      {/* Search Form */}
      <Card className="border-[#E5E4E0] bg-white">
        <CardContent className="p-6">
          <form onSubmit={handleSearch} className="flex gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#5C5F5C]" />
              <Input
                type="text"
                placeholder={searchMode === 'live' 
                  ? "Ask any veterinary neurology question..." 
                  : "Search for neurological conditions, diagnostics, or treatments..."
                }
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-10 bg-[#F8F7F4] border-[#E5E4E0] focus:ring-[#4B6651]/30 focus:border-[#4B6651]"
                data-testid="search-input"
              />
            </div>
            <Button 
              type="submit" 
              className={`${searchMode === 'live' ? 'bg-[#C06144] hover:bg-[#C06144]/90' : 'bg-[#4B6651] hover:bg-[#3E5543]'} text-white`}
              disabled={loading || !query.trim()}
              data-testid="search-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : searchMode === 'live' ? (
                <>
                  <Sparkles className="w-4 h-4 mr-2" />
                  AI Search
                </>
              ) : (
                'Search'
              )}
            </Button>
          </form>

          {/* Suggested Searches */}
          {!searched && (
            <div className="mt-4">
              <p className="text-sm text-[#5C5F5C] mb-2">Suggested searches:</p>
              <div className="flex flex-wrap gap-2">
                {suggestedSearches.map((term) => (
                  <button
                    key={term}
                    onClick={() => setQuery(term)}
                    className="px-3 py-1.5 text-sm bg-[#F8F7F4] text-[#5C5F5C] rounded-full hover:bg-[#4B6651]/10 hover:text-[#4B6651] transition-colors"
                    data-testid={`suggestion-${term.replace(' ', '-')}`}
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {loading ? (
        <div className="flex flex-col items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#4B6651] mb-4" />
          <p className="text-[#5C5F5C]">
            {searchMode === 'live' ? 'AI searching .edu resources...' : 'Searching .edu resources...'}
          </p>
        </div>
      ) : searched && results.length > 0 ? (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-[#5C5F5C]">
              Found {results.length} result{results.length !== 1 ? 's' : ''} for "{query}"
            </p>
            {searchSource && (
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                searchSource === 'live' 
                  ? 'bg-[#C06144]/10 text-[#C06144]' 
                  : 'bg-[#4B6651]/10 text-[#4B6651]'
              }`}>
                {searchSource === 'live' ? 'AI-Powered' : 'Curated'}
              </span>
            )}
          </div>
          {results.map((result, idx) => (
            <Card 
              key={`result-${result.source || ''}-${result.title?.slice(0,30) || idx}`} 
              className="border-[#E5E4E0] bg-white card-hover"
              data-testid={`result-${idx}`}
            >
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    searchSource === 'live' ? 'bg-[#C06144]/10' : 'bg-[#4B6651]/10'
                  }`}>
                    {searchSource === 'live' ? (
                      <Sparkles className="w-5 h-5 text-[#C06144]" />
                    ) : (
                      <GraduationCap className="w-5 h-5 text-[#4B6651]" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-1">
                      {result.title}
                    </h3>
                    <p className="text-sm text-[#4B6651] mb-2">{result.source}</p>
                    <p className="text-[#5C5F5C] mb-4 whitespace-pre-wrap">{result.content}</p>
                    {result.relevance && (
                      <p className="text-xs text-[#5C5F5C] italic mb-3">
                        Relevance: {result.relevance}
                      </p>
                    )}
                    {result.url && result.url !== '#' && (
                      <a
                        href={result.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-sm text-[#4B6651] hover:text-[#3E5543] font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Visit source
                      </a>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : searched ? (
        <Card className="border-[#E5E4E0] bg-white">
          <CardContent className="p-12 text-center">
            <BookOpen className="w-12 h-12 text-[#5C5F5C] mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#2A2C2A] font-['Work_Sans'] mb-2">
              No results found
            </h3>
            <p className="text-[#5C5F5C]">
              Try adjusting your search terms or switch to {searchMode === 'live' ? 'curated' : 'live AI'} search
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-[#E5E4E0] bg-white">
          <CardHeader>
            <CardTitle className="text-lg font-['Work_Sans'] flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-[#4B6651]" />
              About .edu Resources
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-[#5C5F5C] mb-4">
              Our search draws from curated and AI-synthesized veterinary neurology content from accredited 
              educational institutions including:
            </p>
            <ul className="space-y-2 text-[#5C5F5C]">
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4B6651] rounded-full" />
                NC State College of Veterinary Medicine
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4B6651] rounded-full" />
                Auburn University College of Veterinary Medicine
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4B6651] rounded-full" />
                UC Davis School of Veterinary Medicine
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4B6651] rounded-full" />
                Cornell University College of Veterinary Medicine
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4B6651] rounded-full" />
                Purdue University Veterinary Teaching Hospital
              </li>
              <li className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#4B6651] rounded-full" />
                Colorado State University Veterinary Teaching Hospital
              </li>
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default EduSearch;
